import React from "react";
import {
  AnalyticsSection,
  StatCard,
  BarChart,
  PieChart,
} from "./ChartComponents";
import { months, invoicingData } from "./analyticsData";

const InvoicingAnalytics = () => {
  return (
    <div className="space-y-8">
      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
        <StatCard
          title="Total Invoiced to Date"
          value={invoicingData.totalInvoiced}
        />
        <StatCard title="Total Paid to Date" value={invoicingData.totalPaid} />
        <StatCard title="Receivables" value={invoicingData.outstanding} />
        <StatCard
          title="30 Days Overdue Amount"
          value={invoicingData.overdue}
          backgroundColor="bg-red-50"
          textColor="text-red-700"
          borderColor="border-red-200"
        />
      </div>

      {/* Payment Status and Monthly Invoicing */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <AnalyticsSection title="Payment status">
          <div className="flex flex-col lg:flex-row lg:items-center">
            <PieChart
              segments={invoicingData.paymentStatus}
              height={450}
              segmentValueLabel="dollars"
            />
          </div>
        </AnalyticsSection>

        <AnalyticsSection title="Monthly invoicing">
          <BarChart
            data={invoicingData.monthlyInvoicing}
            categories={months}
            height={250}
          />

          <div className="mt-4 grid grid-cols-3 gap-4">
            <div className="text-center">
              <p className="text-xs text-gray-500">Total Invoiced to Date</p>
              <p className="text-xl font-bold text-cpaGold">
                $
                {invoicingData.monthlyInvoicing[0].data
                  .reduce((a, b) => a + b, 0)
                  .toLocaleString()}
              </p>
            </div>
            <div className="text-center">
              <p className="text-xs text-gray-500">Total Collected to Date</p>
              <p className="text-xl font-bold text-cpaGold">
                $
                {invoicingData.monthlyInvoicing[1].data
                  .reduce((a, b) => a + b, 0)
                  .toLocaleString()}
              </p>
            </div>
            <div className="text-center">
              <p className="text-xs text-gray-500">Collection Rate</p>
              <p className="text-xl font-bold text-cpaGold">
                {Math.round(
                  (invoicingData.monthlyInvoicing[1].data.reduce(
                    (a, b) => a + b,
                    0
                  ) /
                    invoicingData.monthlyInvoicing[0].data.reduce(
                      (a, b) => a + b,
                      0
                    )) *
                    100
                )}
                %
              </p>
            </div>
          </div>
        </AnalyticsSection>
      </div>

      {/* Overdue Invoices by Type */}
      <AnalyticsSection title="Overdue invoices by type">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr>
                  <th className="px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Count
                  </th>
                  <th className="px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Amount
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {invoicingData.overdueInvoicesByType.map((item, index) => (
                  <tr key={index}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {item.type}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {item.count}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-red-600 font-medium">
                      ${item.amount.toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900">
                    Total
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900">
                    {invoicingData.overdueInvoicesByType.reduce(
                      (sum, item) => sum + item.count,
                      0
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-red-600 font-bold">
                    $
                    {invoicingData.overdueInvoicesByType
                      .reduce((sum, item) => sum + item.amount, 0)
                      .toLocaleString()}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
          <div>
            <PieChart
              segments={invoicingData.overdueInvoicesByType.map((item) => ({
                name: item.type,
                value: item.amount,
                color: item.color,
              }))}
              height={200}
              segmentValueLabel="dollars"
            />
          </div>
        </div>
      </AnalyticsSection>

      {/* Overdue Invoices Detail */}
      <AnalyticsSection title="Overdue invoices detail">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead>
              <tr>
                <th className="px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Member
                </th>
                <th className="px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Invoice #
                </th>
                <th className="px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Due Date
                </th>
                <th className="px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Amount
                </th>
                <th className="px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Days Overdue
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {invoicingData.overdueInvoices.map((invoice, index) => (
                <tr key={index}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {invoice.member}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {invoice.invoice}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {invoice.dueDate}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {invoice.type}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-red-600 font-medium">
                    {invoice.amount}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800">
                      {invoice.daysOverdue} days
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </AnalyticsSection>

      {/* Invoice Analytics */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <AnalyticsSection title="Invoice types">
          <div className="space-y-4">
            {[
              {
                type: "Membership Dues",
                count: 65,
                amount: "$78,450",
                percentage: "55%",
              },
              {
                type: "Event Registration",
                count: 42,
                amount: "$32,150",
                percentage: "22%",
              },
              {
                type: "Event Sponsorships",
                count: 12,
                amount: "$24,500",
                percentage: "17%",
              },
            ].map((item, index) => (
              <div
                key={index}
                className="flex items-center justify-between p-4 bg-gray-50 rounded-lg"
              >
                <div>
                  <p className="font-medium text-gray-900">{item.type}</p>
                  <p className="text-sm text-gray-500">{item.count} invoices</p>
                </div>
                <div className="text-right">
                  <p className="font-medium text-gray-900">{item.amount}</p>
                  <p className="text-sm text-cpaGold">{item.percentage}</p>
                </div>
              </div>
            ))}
          </div>
        </AnalyticsSection>

        <AnalyticsSection title="Payment methods">
          <div>
            <PieChart
              segments={[
                { name: "Credit Card", value: 68, color: "#3B82F6" },
                { name: "Bank Transfer", value: 23, color: "#10B981" },
                { name: "Cheque", value: 7, color: "#F59E0B" },
                { name: "Cash", value: 2, color: "#8B5CF6" },
                { name: "E-Transfer", value: 1, color: "#EF4444" },
              ]}
              height={250}
              segmentValueLabel="percent"
            />

            <div className="mt-6 grid grid-cols-2 gap-4">
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <p className="text-xs text-gray-500 mb-1">
                  Average Invoice Amount
                </p>
                <p className="text-xl font-bold text-cpaGold">$1,285</p>
              </div>
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <p className="text-xs text-gray-500 mb-1">
                  Average Days to Pay
                </p>
                <p className="text-xl font-bold text-cpaGold">14.3 days</p>
              </div>
            </div>
          </div>
        </AnalyticsSection>
      </div>
    </div>
  );
};

export default InvoicingAnalytics;
